import {
  Badge,
  BodyText,
  Card,
  Flex,
  Heading,
  IconButton,
  InlineLink,
} from '@utilitywarehouse/hearth-react';
import {
  ChevronLeftSmallIcon,
  ChevronRightSmallIcon,
} from '@utilitywarehouse/hearth-react-icons';
import { JSX, ReactNode, useEffect, useRef } from 'react';

import { Carousel, CarouselRef } from '@components/Carousel';
import { scrollToFAQTopic } from '@components/FAQ/scrollToTopic';
import { RetailPartner } from '@generated/gql/graphql';
import { useIsInView } from '@hooks/useIsInView';
import { useResponsiveSlidesToShow } from '@hooks/useResponsiveSlidesToShow';
import { useTrackingContext } from '@tracking/Provider/Context';
import { useTrackSignupCbcRetailPartnerCarouselDisplayedOnce } from '@tracking/trackingEvents';
import { formatNumber } from '@utils/format';

import { Topics } from '../CashbackFAQ/constants';
import './styles.scss';
import { createBreakpoints } from './utils/createBreakpoints';
import { padPartnersToFillRows } from './utils/padPartnersToFillRows';

interface RetailPartnersProps {
  partners: RetailPartner[];
  onFAQLinkClick: () => void;
  Wrapper?: ({ children }: { children: ReactNode }) => JSX.Element;
}

const CAROUSEL_ROWS_SMALL = 4;
const CAROUSEL_ROWS_LARGE = 3;

const breakpoints = createBreakpoints();

const RetailPartners = ({
  partners,
  onFAQLinkClick,
  Wrapper = ({ children }) => <>{children}</>,
}: RetailPartnersProps) => {
  const sectionRef = useRef<HTMLDivElement>(null);
  const isInView = useIsInView(sectionRef);
  const sliderRef = useRef<CarouselRef | null>(null);

  const { trackingContexts } = useTrackingContext();
  const trackSectionDisplayed =
    useTrackSignupCbcRetailPartnerCarouselDisplayedOnce();
  const slidesToShow = useResponsiveSlidesToShow(50, breakpoints);
  const rows = slidesToShow > 5 ? CAROUSEL_ROWS_LARGE : CAROUSEL_ROWS_SMALL;

  useEffect(() => {
    if (isInView) {
      trackSectionDisplayed({}, { contexts: trackingContexts });
    }
  }, [isInView, trackSectionDisplayed, trackingContexts]);

  if (!partners?.length) return null;

  const paddedPartners = padPartnersToFillRows(partners, rows, slidesToShow);

  return (
    <div className="cashback__partners" ref={sectionRef}>
      <Wrapper>
        <Heading as="h2" size="lg">
          Unlimited cashback at your favourite brands
        </Heading>
        <BodyText>
          Earn unlimited cashback at the highest rates when you spend with our
          retail partners, <br className="desktop-only" /> and earn{' '}
          <InlineLink asChild>
            <button
              onClick={() =>
                scrollToFAQTopic(Topics.cashbackEveryTime, onFAQLinkClick)
              }
              type="button"
            >
              1% everywhere else.
            </button>
          </InlineLink>
        </BodyText>
      </Wrapper>

      <Carousel
        ref={sliderRef}
        id="cashback__retail-partners-carousel"
        settings={{
          slidesToShow,
          slidesToScroll: 1,
          rows,
          infinite: true,
          centerMode: true,
          arrows: false,
        }}
      >
        {paddedPartners.map((partner, index) => {
          const percentage = `${formatNumber(partner.discount * 100)}%`;

          return (
            <div className="cashback__partner" key={`${partner.id}-${index}`}>
              <div className="cashback__partner__content">
                <Card
                  variant="emphasis"
                  colorScheme="neutralStrong"
                  position="relative"
                  paddingNone
                  className="cashback__partner__card"
                  aria-label={`${partner.name}, ${percentage}`}
                >
                  <img
                    alt=""
                    className="cashback__partner__logo"
                    src={partner.logoUrl}
                  />

                  <Badge
                    variant="subtle"
                    colorScheme="cashback"
                    size="md"
                    flatBase
                    className="cashback__partner__badge"
                  >
                    {percentage}
                  </Badge>
                </Card>
              </div>
            </div>
          );
        })}
      </Carousel>

      <Flex direction="row" gap="350" marginX="auto">
        <IconButton
          variant="emphasis"
          colorScheme="highlight"
          label="Left"
          onClick={() => sliderRef.current?.slickPrev()}
        >
          <ChevronLeftSmallIcon />
        </IconButton>

        <IconButton
          variant="emphasis"
          colorScheme="highlight"
          label="Right"
          onClick={() => sliderRef.current?.slickNext()}
        >
          <ChevronRightSmallIcon />
        </IconButton>
      </Flex>
    </div>
  );
};

export { RetailPartners };
